import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ChevronDown,
  Save,
  RotateCcw,
  Settings as SettingsIcon,
  Clock,
  FolderKanban,
  Bell,
  Zap,
  History,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { configService, type SystemConfig } from '@/services/api';

const CONFIG_TYPE_ICONS: Record<string, React.ReactNode> = {
  task: <SettingsIcon size={20} />,
  project: <FolderKanban size={20} />,
  notification: <Bell size={20} />,
  performance: <Zap size={20} />,
  system: <Clock size={20} />,
};

const CONFIG_TYPE_LABELS: Record<string, string> = {
  task: 'Task Settings',
  project: 'Project Settings',
  notification: 'Notification Settings',
  performance: 'Performance Settings',
  system: 'System Settings',
};

interface ConfigItemProps {
  config: SystemConfig;
  onUpdate: (key: string, value: unknown) => void;
  onReset: (key: string) => void;
}

const ConfigItem: React.FC<ConfigItemProps> = ({ config, onUpdate, onReset }) => {
  const [localValue, setLocalValue] = useState<string | number | boolean>(config.value as string | number | boolean);
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (newValue: string | number | boolean) => {
    setLocalValue(newValue);
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(config.key, localValue);
    setHasChanges(false);
  };

  const handleReset = () => {
    onReset(config.key);
    setHasChanges(false);
  };

  const renderInput = () => {
    switch (config.valueType) {
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={localValue as boolean}
                onChange={(e) => {
                  handleChange(e.target.checked);
                  onUpdate(config.key, e.target.checked);
                }}
                disabled={!config.isEditable}
              />
            }
            label={localValue ? 'Enabled' : 'Disabled'}
          />
        );

      case 'number':
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              type="number"
              value={localValue}
              onChange={(e) => handleChange(Number(e.target.value))}
              size="small"
              disabled={!config.isEditable}
              inputProps={{
                min: config.minValue,
                max: config.maxValue,
              }}
              sx={{ width: 150 }}
            />
            {config.minValue != null && config.maxValue != null && (
              <Typography variant="caption" color="text.secondary">
                ({config.minValue} - {config.maxValue})
              </Typography>
            )}
          </Box>
        );

      case 'string':
        return (
          <TextField
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
            size="small"
            disabled={!config.isEditable}
            sx={{ minWidth: 200 }}
          />
        );

      default:
        return (
          <TextField
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
            size="small"
            disabled={!config.isEditable}
            fullWidth
          />
        );
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: 'background.default',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {config.displayName}
          </Typography>
          {config.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {config.description}
            </Typography>
          )}
        </Box>
        {!config.isEditable && (
          <Chip label="Read-only" size="small" color="default" sx={{ ml: 2 }} />
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
        {renderInput()}
        {config.isEditable && config.valueType !== 'boolean' && hasChanges && (
          <Button
            size="small"
            variant="contained"
            startIcon={<Save size={14} />}
            onClick={handleSave}
          >
            Save
          </Button>
        )}
        {config.isEditable && (
          <Tooltip title="Reset to default">
            <IconButton size="small" onClick={handleReset} color="warning">
              <RotateCcw size={16} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const ConfigTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [historyDialog, setHistoryDialog] = useState<{ open: boolean; configKey: string | null }>({
    open: false,
    configKey: null,
  });

  const { data: configs, isLoading } = useQuery({
    queryKey: ['configs'],
    queryFn: () => configService.getAllConfigs(),
    staleTime: 60000,
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      configService.updateConfig(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configs'] });
      setSuccessMessage('Configuration updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || 'Failed to update configuration');
      setTimeout(() => setErrorMessage(''), 5000);
    },
  });

  const resetMutation = useMutation({
    mutationFn: (key: string) => configService.resetConfig(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configs'] });
      setSuccessMessage('Configuration reset to default');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || 'Failed to reset configuration');
      setTimeout(() => setErrorMessage(''), 5000);
    },
  });

  const handleUpdate = (key: string, value: unknown) => {
    updateMutation.mutate({ key, value });
  };

  const handleReset = (key: string) => {
    resetMutation.mutate(key);
  };

  // Group configs by type
  const groupedConfigs = React.useMemo(() => {
    if (!configs) return {};
    return configs.reduce((acc, config) => {
      if (!acc[config.type]) {
        acc[config.type] = [];
      }
      acc[config.type].push(config);
      return acc;
    }, {} as Record<string, SystemConfig[]>);
  }, [configs]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        System Configuration
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        These settings control system-wide behavior. Changes take effect immediately for all users.
      </Alert>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {Object.entries(groupedConfigs).map(([type, typeConfigs]) => (
          <Accordion key={type} defaultExpanded>
            <AccordionSummary expandIcon={<ChevronDown />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {CONFIG_TYPE_ICONS[type] || <SettingsIcon size={20} />}
                <Typography variant="subtitle1" fontWeight={600}>
                  {CONFIG_TYPE_LABELS[type] || type}
                </Typography>
                <Chip label={typeConfigs.length} size="small" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {typeConfigs.map((config) => (
                  <ConfigItem
                    key={config.id}
                    config={config}
                    onUpdate={handleUpdate}
                    onReset={handleReset}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default ConfigTab;
